import React, { useEffect } from 'react';
import { Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';

import KEY_STUDENT_ENGAGEMENT_QUERY from 'graph/reports/key-student-engagement-report-query';
import useReportQueryParams from '../../../../hooks/use-report-query-params';
import StudentSpotCheckChart from './StudentSpotCheckChart';
import StudentAssessmentChart from './StudentAssessmentChart';
import StudentEnrichmentActivityChart from './StudentEnrichmentActivityChart';
import SpotCheckTable from './SpotCheckTable';
import AssessmentTable from './AssessmentTable';
import EnrichmentActivityTable from './EnrichmentActivityTable';
import { getQueryVariables } from 'components/ReportsModule/utils/report-param-utils';
import { getIntervalDetails, resolveDateRange } from '../../../../utils/report-date-utils';
import { KeyStudentEngagementReportResult } from 'types/schema-generated-types';
import { ReportView } from '../../../../types';
import StudentAssessmentAcceleratorTableCard from './StudentAccessmentAcceleratorTableCard';
import StudentAssessmentAcceleratorChart from './StudentAssessmentAcceleratorChartCard';

const EngagementCards: React.FC = () => {
  const [{ entityId, entityType, dateRange, viewAs }] = useReportQueryParams();
  const { t } = useTranslation('Reports');
  const [fetchReport, { data, loading }] = useLazyQuery(KEY_STUDENT_ENGAGEMENT_QUERY);
  const reportData: KeyStudentEngagementReportResult = data?.keyStudentEngagementReport ?? {};

  const { dateStart, dateEnd } = resolveDateRange(dateRange);
  const { intervals, dateFormat } = getIntervalDetails(dateStart, dateEnd);

  useEffect(() => {
    fetchReport({ variables: getQueryVariables({ entityId, entityType, dateRange }) });
  }, [fetchReport, entityId, entityType, dateRange]);

  const chartView = (
    <>
      <StudentSpotCheckChart
        dateIntervals={intervals}
        dateIntervalFormat={dateFormat}
        reportData={reportData.spotChecks ?? []}
        loading={loading}
      />
      <StudentAssessmentChart
        dateIntervals={intervals}
        dateIntervalFormat={dateFormat}
        reportData={reportData.assessments ?? []}
        loading={loading}
      />
      <StudentAssessmentAcceleratorChart
        loading={loading}
        reportData={{ ...reportData.assessmentAccelerator, student: reportData.student } as any}
      />
      <StudentEnrichmentActivityChart
        dateIntervals={intervals}
        dateIntervalFormat={dateFormat}
        reportData={reportData.enrichmentActivities ?? []}
        loading={loading}
      />
    </>
  );

  const tableView = (
    <>
      <SpotCheckTable reportData={[...(reportData.spotChecks ?? [])]} loading={loading} />
      <AssessmentTable reportData={[...(reportData.assessments ?? [])]} loading={loading} />
      <StudentAssessmentAcceleratorTableCard
        reportData={{ ...reportData.assessmentAccelerator, student: reportData.student } as any}
        loading={loading}
      />
      <EnrichmentActivityTable
        reportData={[...(reportData.enrichmentActivities ?? [])]}
        loading={loading}
      />
    </>
  );

  const REPORT_VIEW_MAP: { [key in ReportView]?: any } = {
    [ReportView.CHART]: chartView,
    [ReportView.TABLE]: tableView
  };

  return (
    <section>
      <Typography variant="h3">{t('Engagement')}</Typography>
      {REPORT_VIEW_MAP[viewAs]}
    </section>
  );
};

export default EngagementCards;
